/**
 * Where the visitor came from before reaching the compare tool.
 *
 * `document.referrer` alone cannot answer this: App Router navigations are
 * client-side, so after home -> firm detail -> compare the referrer still points at
 * whatever loaded the very first page. The previous in-app path therefore has to be
 * recorded as the visitor moves, which `RouteHistory` does from the root layout.
 */

const CURRENT_KEY = "pr_current_path";
const PREVIOUS_KEY = "pr_previous_path";

const SEARCH_ENGINE = /(^|\.)(google|bing|duckduckgo|yahoo|yandex|baidu|ecosia|brave)\./i;

/** Called on every route change. Cheap and idempotent for repeat calls on one path. */
export function recordVisitedPath(pathname) {
  if (typeof window === "undefined" || !pathname) return;

  try {
    const current = window.sessionStorage.getItem(CURRENT_KEY);
    // The compare tool rewrites its own query string on every dropdown change; the
    // pathname does not move, so the entry point must not be overwritten.
    if (current === pathname) return;

    if (current) window.sessionStorage.setItem(PREVIOUS_KEY, current);
    window.sessionStorage.setItem(CURRENT_KEY, pathname);
  } catch {
    // Private mode / storage disabled — source just falls back to the referrer.
  }
}

export function getPreviousPath() {
  if (typeof window === "undefined") return null;

  try {
    return window.sessionStorage.getItem(PREVIOUS_KEY);
  } catch {
    return null;
  }
}

/** Map a site path to one of the API's `source` values. */
function sourceFromPath(pathname) {
  if (!pathname || pathname === "/") return "home_page";
  if (pathname.startsWith("/prop-firm-details")) return "firm_detail";
  if (pathname.startsWith("/compare")) return "compare_tool";
  if (pathname.startsWith("/blog")) return "blog";
  return "other";
}

function sourceFromReferrer(referrer) {
  if (!referrer) return "direct";

  let url;
  try {
    url = new URL(referrer);
  } catch {
    return "direct";
  }

  if (SEARCH_ENGINE.test(url.hostname)) return "search";

  // The WordPress blog lives on a subdirectory of this host, so it arrives as a
  // same-host referrer with a /blog path rather than as an in-app navigation.
  if (url.host === window.location.host) return sourceFromPath(url.pathname);

  return "other";
}

/** Best available entry point for the current compare visit. */
export function resolveCompareSource() {
  if (typeof window === "undefined") return "direct";

  const previous = getPreviousPath();
  if (previous) return sourceFromPath(previous);

  return sourceFromReferrer(document.referrer);
}
